import styled, { css, keyframes } from "styled-components";
import { SPACING, ANIMATION, OVERLAYS } from "../../../utils/constants";
import { AmountVariant, amountVariantColor } from "../../../utils/tone";
import { ConfirmTone } from "./types";
import { BaseButton } from "../../Button";

/* ------------------------------------------------------------------ *
 * Tone palette — maps a confirmation tone to its CSS color tokens.
 * Shared by DeletePopUp (danger) and DiscardPopUp (warning).
 * ------------------------------------------------------------------ */

const TONE_COLORS: Record<ConfirmTone, { background: string; color: string }> =
  {
    danger: {
      background: "var(--color-error-background)",
      color: "var(--color-error)",
    },
    warning: {
      background: "var(--color-warning-background)",
      color: "var(--color-warning)",
    },
  };

export const tone = (value: ConfirmTone = "danger") => TONE_COLORS[value];

/* ------------------------------------------------------------------ *
 * Animations
 * ------------------------------------------------------------------ */

const backdropFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const dialogPopIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const sheetSlideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

/* ------------------------------------------------------------------ *
 * Shared content
 * ------------------------------------------------------------------ */

export const IconBadge = styled.div<{ $tone?: ConfirmTone }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $tone }) => tone($tone).background};
  color: ${({ $tone }) => tone($tone).color};

  svg {
    font-size: var(--text-xxl);
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xl);
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.25;
  color: var(--color-text-default);
`;

export const Description = styled.div`
  margin: 0;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  line-height: 1.55;
  color: var(--color-text-secondary);
  max-width: 52ch;
`;

const PILL_COLORS: Record<
  string,
  { bg: string; border: string; color: string }
> = {
  EXPENSE: {
    bg: "color-mix(in srgb, var(--color-error) 10%, transparent)",
    border: "color-mix(in srgb, var(--color-error) 35%, transparent)",
    color: "var(--color-error)",
  },
  INCOME: {
    bg: "color-mix(in srgb, var(--color-success) 12%, transparent)",
    border: "color-mix(in srgb, var(--color-success) 40%, transparent)",
    color: "var(--color-success)",
  },
  LOAN: {
    bg: "color-mix(in srgb, var(--color-warning) 12%, transparent)",
    border: "color-mix(in srgb, var(--color-warning) 40%, transparent)",
    color: "var(--color-warning)",
  },
  TRANSFER: {
    bg: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
    border: "color-mix(in srgb, var(--color-primary) 35%, transparent)",
    color: "var(--color-primary)",
  },
  DEFAULT: {
    bg: "color-mix(in srgb, var(--color-text-secondary) 10%, transparent)",
    border: "color-mix(in srgb, var(--color-text-secondary) 30%, transparent)",
    color: "var(--color-text-secondary)",
  },
};

export const DetailsPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.lg}px;
  padding: ${SPACING.md}px;
  border-radius: 10px;
  background: var(--hover-background);
  border: 1px solid var(--color-border);
  max-width: 110%;
`;

export const DetailsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const StatusDot = styled.span<{ $tone?: ConfirmTone }>`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $tone }) => tone($tone).color};
`;

export const DetailIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  background: color-mix(in srgb, var(--color-primary) 12%, transparent);
  color: var(--color-primary);

  svg {
    font-size: var(--text-xl);
  }
`;

export const DetailPill = styled.span<{ $variant: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACING.xs}px ${SPACING.sm + SPACING.xs}px;
  border-radius: 999px;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  background: ${({ $variant }) =>
    (PILL_COLORS[$variant] ?? PILL_COLORS.DEFAULT).bg};
  border: 1px solid
    ${({ $variant }) => (PILL_COLORS[$variant] ?? PILL_COLORS.DEFAULT).border};
  color: ${({ $variant }) =>
    (PILL_COLORS[$variant] ?? PILL_COLORS.DEFAULT).color};
`;

export const DetailTextStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
  text-align: left;
`;

export const DetailTitle = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-text-default);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DetailSubtitle = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  line-height: 1.3;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: wrap;
`;

export const DetailAmount = styled.span<{
  $variant?: AmountVariant;
}>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: ${({ $variant }) => amountVariantColor($variant)};
`;

/**
 * Inline error surface shown inside the dialog/sheet body when a confirmed
 * action fails. Uses the shared error tokens so it reads as destructive
 * feedback without a hardcoded palette. Callers give it `role="alert"`.
 */
export const ErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  border-radius: 10px;
  background: var(--color-error-background);
  border: 1px solid var(--color-error-border);
  color: var(--color-error);
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  line-height: 1.45;
  text-align: left;

  svg {
    flex-shrink: 0;
    font-size: var(--text-xl);
  }
`;

/* ------------------------------------------------------------------ *
 * Web — centered dialog (>= tabletLarge, 850px)
 * ------------------------------------------------------------------ */

export const DialogBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACING.lg}px;
  background: ${OVERLAYS.backdropLight};
  backdrop-filter: blur(2px);
  animation: ${backdropFadeIn} ${ANIMATION.duration.normal}
    ${ANIMATION.easing.easeOut};
`;

export const Dialog = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  background: var(--color-background-2);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: ${dialogPopIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOutQuint};
`;

// Desktop-only; the mobile sheet relies on its cancel button.
export const CloseSlot = styled.div`
  position: absolute;
  top: ${SPACING.md}px;
  right: ${SPACING.md}px;
`;

export const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg}px;
  padding: ${SPACING.lg}px;
`;

export const DialogHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.md}px;
`;

export const HeadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding-top: ${SPACING.xxs}px;
`;

export const DialogFooter = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-start;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px ${SPACING.lg}px;
  background: var(--hover-background);
  border-top: 1px solid var(--color-border);
`;

/* ------------------------------------------------------------------ *
 * Mobile / small tablet — bottom action sheet (< tabletLarge, 850px)
 * ------------------------------------------------------------------ */

export const SheetBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: ${SPACING.sm}px;
  padding-bottom: max(${SPACING.sm}px, env(safe-area-inset-bottom));
  background: ${OVERLAYS.backdropLight};
  animation: ${backdropFadeIn} ${ANIMATION.duration.normal}
    ${ANIMATION.easing.easeOut};
`;

export const SheetGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  width: 100%;
  animation: ${sheetSlideUp} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOutQuint};
`;

export const SheetCard = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--color-background-2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
`;

export const SheetBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.lg}px;
  text-align: center;
`;

const sheetButtonText = css`
  justify-content: center;
  width: 100%;
  min-height: 56px;
  padding: ${SPACING.md}px;
  border: none;
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: background ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};
`;

export const SheetActionButton = styled(BaseButton)<{ $tone?: ConfirmTone }>`
  ${sheetButtonText}
  border-top: 1px solid var(--color-border);
  background: transparent;
  color: ${({ $tone }) => tone($tone).color};

  &:hover:not(:disabled),
  &:active:not(:disabled) {
    background: ${({ $tone }) => tone($tone).background};
  }

  /* Inset ring: SheetCard's overflow clips an outer one. */
  &:focus-visible {
    box-shadow: none;
    outline: 2px solid ${({ $tone }) => tone($tone).color};
    outline-offset: -2px;
  }
`;

/**
 * Wrapper for the optional "Don't ask me again" checkbox row.
 */
export const DontAskWrapper = styled.div`
  display: flex;
  align-items: center;
  color: var(--color-text-secondary);
  min-height: 44px;
`;

export const SheetCancelButton = styled(BaseButton)`
  ${sheetButtonText}
  border-radius: 16px;
  background: var(--color-background-2);
  color: var(--color-primary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);

  &:hover:not(:disabled),
  &:active:not(:disabled) {
    background: var(--color-primary);
    color: var(--color-white);
  }

  &:focus-visible {
    box-shadow:
      var(--shadow-focus),
      0 8px 24px rgba(0, 0, 0, 0.18);
  }
`;
